import { observationLabel } from './einvoice-observation-notice'
import { useEInvoiceObservation } from '@/hooks/use-einvoice-observation'
import { Badge } from '@/components/ui/badge'
import { m } from '@/lib/paraglide/messages'

/** Nhóm nghiệp vụ người vận hành cần phân biệt trên danh sách đơn. */
export type EInvoiceGroup =
    | 'unknown'
    | 'none'
    | 'awaiting'
    | 'draft'
    | 'pushed'
    | 'processing'
    | 'duplicated'
    | 'issuing'
    | 'rejected'
    | 'failed'
    | 'issued'
    | 'manualIssued'
    | 'skipped'

// pending và created là dữ liệu legacy nên vẫn cùng nhóm xử lý. Các trạng thái
// mới giữ riêng vì mỗi trạng thái giao một việc khác nhau cho nhân viên hoặc kế
// toán. Unknown states remain visible without guessing a tax outcome.
//
// Khi CHƯA có dòng nào, cờ issue_einvoice mới là thứ phân biệt: đã tick =
// 'awaiting' (chờ người phát hành), không tick = 'none' (không ai chờ hóa đơn). Gộp hai thứ đó lại thì đơn đã giao quá cửa sổ 3 ngày — nhóm thật sự
// bị bỏ quên — trông y hệt đơn khách lẻ và không ai nhặt ra được.
export function einvoiceGroupOf(
    status?: string,
    issueEInvoice?: boolean,
): EInvoiceGroup {
    switch (status) {
        case 'draft':
            return 'draft'
        case 'pushed':
            return 'pushed'
        case 'publishing':
        case 'publish_unknown':
        case 'pending':
        case 'created':
            return 'processing'
        // MISA đã trả lời là hóa đơn tồn tại: không còn là chờ xử lý, mà là
        // việc kế toán phải tra trên meInvoice.
        case 'publish_duplicated':
            return 'duplicated'
        case 'issuing':
            return 'issuing'
        case 'rejected':
            return 'rejected'
        case 'publish_failed':
        case 'failed':
            return 'failed'
        case 'issued':
            return 'issued'
        case 'manual_issued':
            return 'manualIssued'
        case 'skipped':
            return 'skipped'
        default:
            return status ? 'unknown' : issueEInvoice ? 'awaiting' : 'none'
    }
}

export const EINVOICE_GROUP_LABELS: Record<EInvoiceGroup, () => string> = {
    unknown: m.einvoice_badge_unknown,
    none: m.einvoice_badge_none,
    awaiting: m.einvoice_badge_awaiting,
    draft: m.einvoice_badge_draft,
    pushed: m.einvoice_badge_pushed,
    processing: m.einvoice_badge_processing,
    duplicated: m.einvoice_badge_duplicated,
    issuing: m.einvoice_badge_issuing,
    rejected: m.einvoice_badge_rejected,
    failed: m.einvoice_badge_failed,
    issued: m.einvoice_badge_issued,
    manualIssued: m.einvoice_badge_manual_issued,
    skipped: m.einvoice_badge_skipped,
}

// Cùng bảng màu với PaymentStatusBadge để một hàng đơn không thành cầu vồng.
// Nháp = hổ phách, lỗi/từ chối = đỏ, đã cấp mã = xanh lá, chưa xuất = xám.
const EINVOICE_GROUP_COLORS: Record<EInvoiceGroup, string> = {
    unknown: 'bg-muted text-foreground',
    none: 'bg-muted text-muted-foreground',
    // Xám nhạt có viền chữ đậm hơn 'none': có việc sẽ tới, nhưng chưa phải việc
    // của nhân viên lúc này — không được nổi bằng 'Nháp' (việc phải làm ngay).
    awaiting:
        'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    pushed: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
    processing:
        'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    duplicated: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    issuing:
        'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    issued: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    manualIssued: 'bg-muted text-foreground',
    skipped: 'bg-muted text-muted-foreground',
}

interface BadgeProps {
    status?: string
    issueEInvoice?: boolean
    orderId?: string
}
export function EInvoiceStatusBadge(props: BadgeProps) {
    return props.orderId ? (
        <ObservedBadge {...props} orderId={props.orderId} />
    ) : (
        <WorkflowBadge {...props} />
    )
}
function ObservedBadge(props: BadgeProps & { orderId: string }) {
    const observation = useEInvoiceObservation(props.orderId)
    if (!observation) return <WorkflowBadge {...props} />
    const warning =
        observation.lifecycle.is_deleted ||
        ['rejected', 'send_error'].includes(observation.tax.state)
    return (
        <Badge
            className={
                EINVOICE_GROUP_COLORS[
                    warning
                        ? 'rejected'
                        : observation.publication === 'published'
                          ? 'issued'
                          : 'unknown'
                ]
            }
            title={observation.observed_at ?? undefined}
        >
            {observationLabel(observation)}
        </Badge>
    )
}
function WorkflowBadge({ status, issueEInvoice }: BadgeProps) {
    const group = einvoiceGroupOf(status, issueEInvoice)
    // 'none' = đơn không xuất hóa đơn: KHÔNG hiển thị gì. Đây là mặc định của
    // hầu hết đơn (không tick), một tag xám "Không xuất" chỉ chiếm chỗ vô nghĩa
    // trên bảng. Các nhóm còn lại (kể cả 'awaiting' = chờ phát hành) vẫn hiện vì
    // đều là việc cần theo dõi.
    if (group === 'none') return null
    return (
        <Badge className={EINVOICE_GROUP_COLORS[group]}>
            {EINVOICE_GROUP_LABELS[group]()}
        </Badge>
    )
}
